// src/registered/site-admin/report_management/reportingFlow.js
//
// Single source of truth for the "Manage ESG Reporting" flow.
//   - the numbered tiles on ReportManagementHub
//   - the explainer banner shown at the top of each destination screen
//     (ReportingStepFrame looks a step up by `key`)
//
// The steps are deliberately ordered. A user should be able to work straight
// down 1 → 5. `needs` lists what must exist first (and where it lives);
// `feeds` lists what later steps consume it.

export const REPORTING_STEPS = [
  {
    key: "register-company",
    n: 1,
    name: "Register a Company",
    icon: "/icons/2D2C31_SiteAdmin.png",
    path: "/registered/site-admin/report-management/register-company",
    blurb: "Create the tenant company this ESG reporting belongs to.",
    what:
      "Registers a new company on the platform and makes you its Representative. " +
      "Everything else in this flow — templates, attributes, meter data, and the " +
      "eventual filled reports — attaches to a company, so this comes first.",
    needs: [],
    feeds: ["Every step below is scoped to the company you register here."],
  },
  {
    key: "component-library",
    n: 2,
    name: "Report Component Library",
    icon: "/icons/2D2C31_defineReport.png",
    path: "/registered/site-admin/report-component-library",
    blurb:
      "Define the reusable building blocks — narrative, metric table, chart, disclosure, evidence list.",
    what:
      "A component is a typed building block, defined once and reused across many " +
      "report templates. You first bind the disclosure datapoint(s) it reports " +
      "(from the ingested taxonomy), then label it, choose how it presents " +
      "(render type), and add the input slots a person fills in when it is used. " +
      "Nothing here is company-specific. In the disclosure filter: a space means " +
      "all words must match, OR gives alternatives, -word excludes, \"phrase\" " +
      "matches exactly and * is a wildcard — filtering happens in your browser, " +
      "nothing is sent anywhere.",
    needs: [],
    feeds: ["The report template (article-first, being rebuilt) arranges these into a report."],
  },
  {
    key: "attribute-definitions",
    n: 3,
    name: "Declaration Attribute Definitions",
    icon: "/icons/2D2C31_document.png",
    path: "/registered/site-admin/declr-attribute-definitions",
    blurb:
      "Define the reusable, typed local-capture fields companies fill in (e.g. “Electricity consumption”).",
    what:
      "An attribute definition is a single data field a company captures locally — " +
      "its value type, unit family, default unit and (for enums) its options. These " +
      "are global reference data: companies compose them into attribute groups and " +
      "into a report's data-entry forms.",
    needs: [],
    feeds: [
      "Step 4 maps these to taxonomy concepts.",
      "Step 5 (Meter Data Catalogue) points meter quantities at them so readings fill them automatically.",
    ],
  },
  {
    key: "attribute-mapping",
    n: 4,
    name: "Declaration Attribute Mapping",
    icon: "/icons/2D2C31_document.png",
    path: "/registered/site-admin/declr-attribute-mapping",
    blurb:
      "Map each local attribute to a group taxonomy concept — unit conversion and roll-up method.",
    what:
      "For each attribute definition, records the group taxonomy concept it rolls " +
      "up to, the unit-conversion factor, and the aggregation method (SUM / AVERAGE " +
      "/ LATEST). This is the rule the group roll-up engine applies to every " +
      "company's local declaration values.",
    needs: [
      "Attribute definitions from step 3.",
      "A taxonomy ingested with the XBRL Processor (Site Administration) — you map onto its concepts.",
    ],
    feeds: ["The group roll-up / consolidation of company declarations."],
  },
  {
    key: "meter-catalogue",
    n: 5,
    name: "Meter Data Catalogue",
    icon: "/icons/2D2C31_data-warehouse.png",
    path: "/registered/site-admin/meter-catalogue",
    blurb:
      "Register meter quantities (OBIS and more) and the ESG attributes each reading feeds.",
    what:
      "A protocol-neutral catalogue of utility-meter quantities, their external " +
      "identifiers (OBIS today), and the physical meters registered against a " +
      "company. Each concept links to the attribute definitions it feeds, so a " +
      "resolved reading flows straight into the declaration pipeline without " +
      "manual entry.",
    needs: [
      "Attribute definitions from step 3 — a meter concept has to feed something.",
    ],
    feeds: ["Automated values for the local-capture fields a company would otherwise type in."],
  },
];

// Conducting the actual reporting (filling in Transport / Energy / Water and
// saving report sections) is NOT part of the configure flow — it has its own
// entry point, the "Enter ESG Data" card on the Registered dashboard, routed
// straight to ConductReportingView (no ReportingStepFrame).

// Not part of the numbered flow — an older disclosure-discovery prototype kept
// available until the article-first flow fully replaces it.
export const REPORTING_EXTRAS = [
  {
    key: "report-definition",
    name: "Report Definition (prototype)",
    icon: "/icons/2D2C31_defineReport.png",
    path: "/registered/site-admin/report-definition",
    blurb:
      "Disclosure discovery — search the taxonomy and gather the disclosure elements a report must cover. Folds into step 2 (Report Component Library).",
    what:
      "An earlier prototype for discovering disclosure requirements from a taxonomy. " +
      "Its useful part — the disclosure search — now lives in the Report Component Library " +
      "(step 2). Kept here only until the article-first flow fully replaces it; the " +
      "“Save definition” action is not wired.",
    needs: ["A taxonomy ingested with the XBRL Processor (Site Administration)."],
    feeds: ["Nothing yet — superseded by step 2."],
  },
];

export const stepByKey = (key) =>
  REPORTING_STEPS.find((s) => s.key === key) ||
  REPORTING_EXTRAS.find((s) => s.key === key) ||
  null;
